/**
 * Toast.tsx — substituto SPA do flash() do Flask (plano SPA, §2.8):
 * mensagens de sucesso/erro das mutações viram toasts empilhados.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

export type TipoToast = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: number
  tipo: TipoToast
  mensagem: string
}

interface ContextoToast {
  notificar: (tipo: TipoToast, mensagem: string) => void
}

const Contexto = createContext<ContextoToast | null>(null)

const CORES: Record<TipoToast, string> = {
  success: 'bg-green-600',
  error: 'bg-red-600',
  warning: 'bg-yellow-600',
  info: 'bg-blue-600',
}

export function ProvedorToast({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const proximoId = useRef(1)

  const notificar = useCallback((tipo: TipoToast, mensagem: string) => {
    const id = proximoId.current++
    setToasts((atuais) => [...atuais, { id, tipo, mensagem }])
    setTimeout(() => {
      setToasts((atuais) => atuais.filter((t) => t.id !== id))
    }, 6000)
  }, [])

  const valor = useMemo(() => ({ notificar }), [notificar])

  return (
    <Contexto.Provider value={valor}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="alert"
            className={`${CORES[t.tipo]} max-w-sm rounded-lg px-4 py-3 text-sm text-white shadow-lg`}
          >
            {t.mensagem}
          </div>
        ))}
      </div>
    </Contexto.Provider>
  )
}

export function useToast(): ContextoToast {
  const ctx = useContext(Contexto)
  if (!ctx) throw new Error('useToast fora do ProvedorToast')
  return ctx
}
